import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { UnreadMessage } from '../../types/chat'
import { formatChatDateTime } from '../../utils/datetime_util'

interface NotificationPopupProps {
  unreadMessages: UnreadMessage[]
  onClose: () => void
}

/**
 * 未読通知ポップアップコンポーネント
 * ベルアイコンをクリックすると表示される
 */
const NotificationPopup: React.FC<NotificationPopupProps> = ({
  unreadMessages,
  onClose,
}) => {
  const navigate = useNavigate()
  const popupRef = useRef<HTMLDivElement>(null)

  // ポップアップ外をクリックしたら閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  // 最大10件まで表示
  const displayedMessages = unreadMessages.slice(0, 10)

  const handleMessageClick = (chatRoomId: number) => {
    navigate(`/chat?roomId=${chatRoomId}`)
    onClose()
  }

  return (
    <div
      ref={popupRef}
      className="absolute top-full right-0 mt-2 w-96 bg-main-white rounded-lg shadow-lg border border-main-gray z-50 max-h-96 overflow-hidden flex flex-col"
    >
      {/* ヘッダー */}
      <div className="px-4 py-3 border-b border-main-gray">
        <h3 className="font-bold text-lg">未読通知</h3>
      </div>

      {/* 通知一覧 */}
      <div className="overflow-y-auto flex-1">
        {displayedMessages.length > 0 ? (
          displayedMessages.map((message) => {
            return (
              <div
                key={message.id}
                onClick={() => handleMessageClick(message.chatRoomId)}
                className="px-4 py-3 hover:bg-main-lightgray cursor-pointer border-b border-main-gray transition-colors"
              >
                <div className="flex items-center gap-3">
                  {/* 送信者ロゴ */}
                  <img
                    src={message.senderLogo}
                    alt={message.senderName}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />

                  {/* メッセージ情報 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="font-medium text-sm truncate">
                        {message.senderName}
                      </h4>
                      <span className="text-xs text-main-darkgray ml-2 flex-shrink-0">
                        {formatChatDateTime(message.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-main-darkgray truncate flex-1">
                        {message.content ||
                          (message.attachments.length > 0
                            ? '添付ファイル'
                            : '')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="px-4 py-8 text-center text-main-darkgray">
            未読通知はありません
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationPopup
